import React from 'react';
import { Position, Handle } from 'reactflow';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import NodeHeader from 'src/components/ui/NodeHeader/NodeHeader';
import ActionMenuButton from 'src/components/ui/ActionMenuButton/ActionMenuButton';
import { OptionItemTone } from 'src/components/ui/OptionItem/OptionItem';
import { useNodeIssues } from '../WorkflowValidationContext';
import styles from './WorkflowNodeCard.module.scss';

const HANDLE_STYLE = {
  opacity: '0%',
  width: '1px !important',
  height: '1px !important',
} as const;

const HANDLE_POSITIONS = {
  right: { ...HANDLE_STYLE, top: '35.2px', left: '98%' },
  left: { ...HANDLE_STYLE, top: '50%', left: '2%' },
  top: { ...HANDLE_STYLE, top: '50%' },
  bottom: { ...HANDLE_STYLE, top: '50%' },
} as const;

const NodeHandle: React.FC<{
  id: string;
  position: Position;
  suffix?: string;
}> = ({ id, position, suffix = '' }) => {
  const positionKey = position.toLowerCase() as keyof typeof HANDLE_POSITIONS;
  const handleId = suffix ? `${id}-${suffix}` : id;

  return <Handle type="source" position={position} id={handleId} style={HANDLE_POSITIONS[positionKey]} />;
};

export type WorkflowNodeCardProps = {
  id: string;
  label?: string;
  title: string;
  cardText: string;
  isEditMode: boolean;
  isSelected: boolean;
  isDeletable?: boolean;
  selectedTitle?: string;
  selectedSubtitle?: string;
  selectedIcon?: IconName;
  onCardClick: () => void;
  onResetClick: () => void;
  summary?: React.ReactNode;
  children?: React.ReactNode;
};

const WorkflowNodeCard: React.FC<WorkflowNodeCardProps> = ({
  id,
  label,
  title,
  cardText,
  isEditMode,
  isSelected,
  isDeletable = true,
  selectedTitle,
  selectedSubtitle,
  selectedIcon,
  onCardClick,
  onResetClick,
  summary,
  children,
}) => {
  const { t } = useTranslation();
  const issues = useNodeIssues(id);

  const menuItems = [
    ...(isSelected
      ? [
          {
            label: t('general.edit'),
            accessoryItem: 'edit',
            onClick: () => onCardClick(),
          },
        ]
      : []),
    ...(isDeletable
      ? [
          {
            label: t('general.delete'),
            accessoryItem: 'delete',
            tone: OptionItemTone.Destructive,
            isError: true,
            onClick: () => onResetClick(),
          },
        ]
      : []),
  ];

  const showMenu = isEditMode && menuItems.length > 0;

  return (
    <div className={styles.root}>
      {label && (
        <div className="inline-block mb-8">
          <Badge expanded type={BadgeType.Neutral} label={label} />
        </div>
      )}

      <div
        className={classNames(styles.content, {
          [styles.autoHeight]: !isSelected,
          [styles.hasIssue]: issues.length > 0,
        })}>
        <div className="py-16 px-20">
          <NodeHandle id={id} position={Position.Right} />
          <NodeHandle id={id} position={Position.Left} suffix="left" />
          <NodeHandle id={id} position={Position.Top} suffix="top" />
          <NodeHandle id={id} position={Position.Bottom} suffix="bottom" />
          <Handle
            type="target"
            position={Position.Left}
            id={`${id}`}
            style={{ ...HANDLE_STYLE, top: '50%', left: '2%' }}
          />

          {isSelected && selectedTitle && selectedSubtitle && selectedIcon ? (
            <>
              <NodeHeader
                title={selectedTitle}
                subtitle={selectedSubtitle}
                icon={selectedIcon}
                onActionClick={onResetClick}
                isEditMode={isEditMode}
                actionElement={showMenu ? <ActionMenuButton menuItems={menuItems} /> : undefined}
              />
              {summary && <div className={styles.summary}>{summary}</div>}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h2 className="typo-text-16--bold text-full">{title}</h2>
                {showMenu && <ActionMenuButton menuItems={menuItems} />}
              </div>
              <div
                className={styles.startCard}
                onClick={() => isEditMode && onCardClick()}
                onKeyDown={e => e.key === 'Enter' && isEditMode && onCardClick()}
                role="button"
                tabIndex={0}>
                <span className="typo-text-14 text-faded">{cardText}</span>
              </div>
            </>
          )}
          {issues.length > 0 && (
            <div className={styles.issues}>
              {issues.map(issue => (
                <div key={issue} className={styles.issue}>
                  <Icon name="warning" width={14} height={14} />
                  <span>{issue}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};

export default WorkflowNodeCard;
